/**
 * Centralized GA4 product-analytics module.
 *
 * Single spine for all telemetry -- nothing else in the app should call gtag()
 * directly. Uses raw gtag.js (no npm dependency) so there is no private-registry
 * / node14 friction and multi-stream env routing stays simple.
 *
 * Public API:
 *   initTracking()            -- resolve ID by host, inject gtag, wire SPA
 *                                page_views + a one-shot identify. No-op when no
 *                                ID resolves (dev), so local never pollutes real
 *                                streams.
 *   identify({...})           -- set GA4 user_id + user_properties.
 *   trackPage(path, title)    -- send a GA4 page_view.
 *   track(eventName, params)  -- send a named event (auto-stamps env + version).
 *
 * Env separation is by window.location.host -> measurement ID. Prod is isolated
 * on its own property; staging + preview share a non-prod property and are told
 * apart by the `environment` user property.
 */

import store from '../../redux/store'
import history from '../../history'
import { EVENTS, USER_PROPS } from './events'

// host -> { env, id }. IDs come from build-time DefinePlugin (see webpack.config.js).
const HOST_MAP = {
    'controlcenter.invintusmedia.com': { env: 'production', id: process.env.GA4_ID_PROD },
    'controlcenter-stage.invintusmedia.com': { env: 'staging', id: process.env.GA4_ID_NONPROD },
    'controlcenter-preview.invintusmedia.com': { env: 'preview', id: process.env.GA4_ID_NONPROD },
}

const APP_VERSION = process.env.APP_VERSION

// module state
let measurementId = null
let environment = 'development'
let initialized = false
// Non-prod (staging/preview) runs with debug_mode so events surface in GA4
// DebugView without needing the GA Debugger extension. Prod stays clean.
let debugMode = false

const resolveHost = () => {
    const host = (typeof window !== 'undefined' && window.location && window.location.host) || ''
    return HOST_MAP[host] || { env: 'development', id: null }
}

// gtag.js requires the actual `arguments` object on the dataLayer, NOT a real
// array. Pushing an array (e.g. via rest params) loads the script and queues
// entries but silently sends zero beacons to GA4 -- the canonical Google shim
// uses a plain function + `arguments` for this reason. Do not "modernize" it.
function gtag() {
    if (typeof window === 'undefined' || !window.dataLayer) return
    window.dataLayer.push(arguments)
}

/**
 * Normalize a `pathname + search` string into a low-cardinality page path plus
 * a flag of which query keys were present. Query *values* (event IDs, tokens)
 * never reach GA4 -- only the path and the set of param names.
 */
const normalizePath = (rawPath) => {
    if (!rawPath) return { path: '/', queryKeys: '' }
    const [pathname, search = ''] = String(rawPath).split('?')
    let queryKeys = ''
    if (search) {
        queryKeys = search
            .split('&')
            .map(pair => pair.split('=')[0])
            .filter(Boolean)
            .join(',')
    }
    return { path: pathname || '/', queryKeys }
}

/**
 * Inject the gtag.js loader and configure GA4 with manual page_views.
 * Idempotent and a no-op when no measurement ID resolves for the host.
 */
export const initTracking = () => {
    if (initialized) return
    initialized = true

    const resolved = resolveHost()
    environment = resolved.env
    measurementId = resolved.id
    debugMode = environment !== 'production'

    if (!measurementId) {
        // development (localhost) or an unmapped host -- stay dark so we never
        // pollute a real stream.
        return
    }

    window.dataLayer = window.dataLayer || []
    window.gtag = gtag

    const loader = document.createElement('script')
    loader.async = true
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    document.head.appendChild(loader)

    gtag('js', new Date())
    gtag('config', measurementId, { send_page_view: false, debug_mode: debugMode })

    // SPA page_views: every navigation -- including through the custom
    // routes/router.js layer -- flows through this single history instance.
    trackPage(window.location.pathname + window.location.search, document.title)
    history.listen((location) => {
        trackPage(location.pathname + location.search, document.title)
    })

    // One-shot identify: fire the first time the user is fully loaded into
    // Redux (data.pending flips false in AuthWrapper after setUser), then
    // unsubscribe. Avoids touching AuthWrapper.
    const unsubscribe = store.subscribe(() => {
        const state = store.getState()
        if (state.data && state.data.pending === false && state.data.user) {
            identifyFromState(state)
            unsubscribe()
        }
    })
}

const identifyFromState = (state) => {
    const user = state.data.user || {}
    const securityRoles = state.data.securityRoles || []
    const role = Array.isArray(securityRoles)
        ? securityRoles.find(r => r.roleID === user.userRole)
        : null
    identify({
        userId: user.userId,
        roleId: user.userRole,
        roleName: role && role.roleName,
        clientId: user.clientId,
    })
    // First post-auth load == a login.
    track(EVENTS.LOGIN, {
        role_name: role && role.roleName,
        client_id: user.clientId,
    })
}

/**
 * Set the GA4 user_id (the userId GUID) and user_properties. PII (email) is
 * never sent -- GA4 ToS forbids it. Role/client/env/version only.
 */
export const identify = ({ userId, roleId, roleName, clientId } = {}) => {
    if (!measurementId) return
    if (userId) {
        gtag('config', measurementId, { user_id: userId, send_page_view: false, debug_mode: debugMode })
    }
    gtag('set', 'user_properties', {
        [USER_PROPS.ROLE_NAME]: roleName || null,
        [USER_PROPS.ROLE_ID]: roleId != null ? String(roleId) : null,
        [USER_PROPS.CLIENT_ID]: clientId != null ? String(clientId) : null,
        [USER_PROPS.ENVIRONMENT]: environment,
        [USER_PROPS.APP_VERSION]: APP_VERSION,
    })
}

/**
 * Send a GA4 page_view for an SPA navigation.
 */
export const trackPage = (rawPath, title) => {
    if (!measurementId) return
    const { path, queryKeys } = normalizePath(rawPath)
    gtag('event', 'page_view', {
        page_path: path,
        page_title: title || undefined,
        page_query_keys: queryKeys || undefined,
        environment,
        app_version: APP_VERSION,
    })
}

/**
 * Send a named event. Auto-stamps environment + app_version so every event
 * carries them without each call site repeating it.
 */
export const track = (eventName, params = {}) => {
    if (!measurementId || !eventName) return
    gtag('event', eventName, {
        ...params,
        environment,
        app_version: APP_VERSION,
    })
}

export { EVENTS } from './events'
